'use client';
import { safeAuthReturn } from '@/shared/lib/auth-return';

import { redirect } from 'next/navigation';
import { useActionState, useEffect, useRef } from 'react';

import { GoToRestoreLink } from '@/entities/auth';
import { useToast } from '@/features/toast';
import { Button, LockIcon, TextFiled, UserIcon } from '@/shared';
import { urlBuilder } from '@/shared/utils';

import { authByLoginAction } from '../action';

type AuthByLogin = {
	onSuccess?: () => void;
	onEvent?: (action: string, outcome?: string) => void;
	redirectPath?: string;
	restoreSlot?: React.ReactNode;
};

export function AuthByLogin({
	onSuccess,
	onEvent,
	redirectPath,
	restoreSlot,
}: AuthByLogin) {
	const { addToast } = useToast();
	const [state, formAction, pending] = useActionState(authByLoginAction, {
		leadParams: {
			eventURL: typeof window !== 'undefined' ? window.location.href : '',
		},
	});

	const reportedState = useRef(state);
	useEffect(() => {
		if (reportedState.current === state) return;
		reportedState.current = state;
		onEvent?.(
			'login_result',
			state.success ? 'success' : state.errors ? 'validation_error' : 'error',
		);
	}, [state, onEvent]);

	useEffect(() => {
		if (state.message) {
			addToast('Ошибка', state.message, 'error');
			return;
		}
		if (state.success) {
			if (onSuccess) {
				return onSuccess();
			}
			return redirect(
				safeAuthReturn(redirectPath) ||
					state.defaultRedirectPath ||
					urlBuilder.courses(),
			);
		}
	}, [addToast, state, onSuccess, redirectPath]);

	return (
		<form
			className="flex flex-1 flex-col gap-8 md:flex-grow-0"
			action={formAction}
			onSubmit={() => onEvent?.('login_start')}
			onInvalidCapture={(event) => {
				if (event.target === event.currentTarget.querySelector(':invalid'))
					onEvent?.('validation_failed', 'login');
			}}
		>
			<fieldset className="mb-auto space-y-3">
				<TextFiled
					type="text"
					name="login"
					placeholder="Логин"
					autoComplete="username"
					icon={<UserIcon className="size-6" />}
					defaultValue={(state.formData?.get('login') || '') as string}
					invalid={!!state?.errors?.login}
					hint={state?.errors?.login?.reduce((_, err) => err, '')}
					size="lg"
					required
				/>

				<TextFiled
					type="password"
					name="password"
					placeholder="Пароль"
					icon={<LockIcon className="size-6" />}
					autoComplete="current-password"
					defaultValue={(state.formData?.get('password') || '') as string}
					invalid={!!state?.errors?.password}
					hint={state?.errors?.password?.reduce((_, err) => err, '')}
					size="lg"
					required
				/>
			</fieldset>

			<Button type="submit" size="lg" loading={pending} block>
				Войти
			</Button>

			<div
				className="text-center"
				onClickCapture={(event) => {
					if (event.target instanceof Element && event.target.closest('a'))
						onEvent?.('password_restore_open');
				}}
			>
				{restoreSlot ?? (
					<GoToRestoreLink redirectPath={safeAuthReturn(redirectPath)} />
				)}
			</div>
		</form>
	);
}
